import { IAction } from '@common/types';
import { ICardList } from '@nocode/types';
import { get, isEmpty } from 'lodash';
import queryString from 'query-string';
import * as React from 'react';
import ContentLoader, { Rect } from 'react-content-loader/native';
import { Platform } from 'react-native';
import {
  Button,
  Card,
  IconButton,
  Paragraph,
  Subheading,
  Title,
} from 'react-native-paper';
import { defaultURL } from '../../../../utils/common.constant';
import { checkFont } from '../../func';
import {
  getActions,
  getImageCardCover,
  getItemListClick,
  getValueBinding,
} from '../../shared';
import { useImageSecurity } from '@common/components/ImageSecurity';

type Props = {
  attrs: ICardList;
  index: number;
  item: any;
  onPress: (actions: IAction[], options: Record<string, any>) => void;
  isWeb?: boolean;
  finalStyle?: any;
};

const CardItem = (props: Props) => {
  const { attrs, index, item, onPress, isWeb, finalStyle } = props;
  const search = queryString.parse(window?.location?.search);
  const target = search?.target;

  const [isLoading, setIsLoading] = React.useState(false);

  const { title, subTitle, subTitle2, cardCover, button1, icon1, icon2 } =
    attrs.attributes;

  const itemMargin = finalStyle?.gap || attrs.attributes?.gap || 2;
  const columnCount = get(attrs.attributes, 'columnCount', 2);
  const cardItemOffset = (attrs.width * 100) / columnCount / 100;

  const imageType = get(cardCover, 'image.imageType');

  const placeholderImageUrl = get(
    attrs,
    'attributes.cardCover.image.binding.options.placeholderImage',
    null
  );

  const placeholderImageEnabled = get(
    attrs,
    'attributes.cardCover.image.binding.options.placeholderImageEnabled',
    false
  );

  const placeholderImage =
    placeholderImageEnabled && imageType !== 'uploaded'
      ? placeholderImageUrl
      : null;

  const cardUrl = getImageCardCover(item, cardCover);

  const placeholderUrl = placeholderImageEnabled
    ? placeholderImage
    : defaultURL;

  const storedCover = !isEmpty(cardUrl) ? cardUrl : placeholderUrl;

  // `getImageCardCover` hands back the raw record object when the binding has
  // no `imageUrl` of its own, and the signer only takes strings. `isEmpty`
  // above does not catch that case - it reports an object with keys as
  // non-empty - so the object reached `source.uri` as it was.
  const storedCoverUrl =
    typeof storedCover === 'string'
      ? storedCover
      : get(storedCover, 'url', null);

  // A card cover is a stored pre-signed S3 URL whose five minutes expired long
  // before anyone opened the list, so it is signed again at render time. The
  // placeholder is signed separately because a builder can upload one, and it
  // is what shows while the cover is still being signed; when none is
  // configured it is `defaultURL`, an inline data: URI the signer returns
  // untouched. Same arrangement as HorizontalCardList/CardItem.
  const { imageUrl: secureCardCover } = useImageSecurity(storedCoverUrl);
  const { imageUrl: securePlaceholder } = useImageSecurity(placeholderUrl);

  const cardCoverImage = secureCardCover || securePlaceholder;

  // Apply finalStyle to card wrapper
  const cardWrapperStyle = {
    width: cardItemOffset - +itemMargin * 2 - (isWeb ? 10 : 0),
    margin: +itemMargin / 2,
    shadowColor: finalStyle?.shadowColor || attrs.attributes.shadow?.color,
    shadowOffset: finalStyle?.shadowOffset || {
      width: attrs.attributes.shadow?.x,
      height: attrs.attributes.shadow?.y,
    },
    shadowOpacity: finalStyle?.shadowOpacity || attrs.attributes.shadow?.blur,
    shadowRadius: finalStyle?.shadowRadius || attrs.attributes.shadow?.size,
    overflow: 'hidden' as const,
    elevation: finalStyle?.elevation || 5,
    opacity: finalStyle?.opacity || attrs.attributes.opacity || 1,
    backgroundColor:
      finalStyle?.backgroundColor || attrs.attributes.backgroundColor,
    borderColor: finalStyle?.borderColor || attrs.attributes.borderColor,
    borderWidth: finalStyle?.borderWidth || attrs.attributes.borderWidth,
    borderRadius: finalStyle?.borderRadius || attrs.attributes.borderRadius,
    borderStyle:
      (finalStyle?.borderStyle || attrs.attributes.borderStyle) !== 'none'
        ? finalStyle?.borderStyle || attrs.attributes.borderStyle
        : undefined,
  };

  const cardCoverStyle = {
    borderRadius:
      cardCover?.styles?.image?.borderRadius || finalStyle?.borderRadius || 0,
    height: isLoading ? 115 : 0,
    display: (isLoading || Platform.OS !== 'web' ? 'flex' : 'none') as any,
  };

  const cardContentStyle = {
    padding: 16,
  };

  const cardActionStyle = {
    width: '100%',
    justifyContent: 'space-between' as const,
    gap: finalStyle?.gap || 8,
  };

  // Apply finalStyle to button styling
  const actionsButton1Style = {
    borderWidth: button1?.border
      ? button1.borderSize
      : finalStyle?.borderWidth || 0,
    borderColor: finalStyle?.borderColor || button1?.borderColor || 'red',
    borderRadius: finalStyle?.borderRadius || button1?.rounding,
    backgroundColor:
      button1.styles?.text?.backgroundColor ||
      finalStyle?.backgroundColor ||
      button1?.backgroundColor ||
      '#4259AC',
    color: button1.styles?.text?.color || finalStyle?.color,
  };

  const buttonLabelStyle = {
    fontSize: finalStyle?.fontSize || 10,
    fontFamily: checkFont(
      finalStyle?.fontFamily || attrs.attributes.fontFamily
    ),
    color: finalStyle?.color,
  };

  // Apply finalStyle to text styles
  const titleTextStyle = {
    fontFamily: checkFont(
      finalStyle?.fontFamily || attrs.attributes.fontFamily
    ),
    fontSize: finalStyle?.fontSize || attrs.attributes.fontSize,
    fontWeight: finalStyle?.fontWeight?.toString() || '500',
    color:
      finalStyle?.color ||
      attrs?.attributes?.title?.styles?.text?.color ||
      attrs.attributes.color,
  };

  const subTitleTextStyle = {
    fontFamily: checkFont(
      finalStyle?.fontFamily || attrs.attributes.fontFamily
    ),
    fontSize: finalStyle?.fontSize || attrs.attributes.fontSize,
    fontWeight: finalStyle?.fontWeight?.toString() || '400',
    color:
      subTitle?.styles?.text?.color ||
      finalStyle?.color ||
      attrs?.attributes?.subTitle?.styles?.text?.color ||
      '#6C777C',
  };

  const subTitle2TextStyle = {
    fontFamily: checkFont(
      finalStyle?.fontFamily || attrs.attributes.fontFamily
    ),
    fontSize: finalStyle?.fontSize || attrs.attributes.fontSize,
    fontWeight: finalStyle?.fontWeight?.toString() || '400',
    color:
      finalStyle?.color ||
      attrs?.attributes?.subTitle2?.styles?.text?.color ||
      '#6C777C',
  };

  const handlePress = (id: string, item: Record<string, any>) => {
    const options = {
      itemListClick: getItemListClick(item.record),
    };

    onPress && onPress(getActions(attrs, id), options);
  };

  const bindingValue = getValueBinding('', item, attrs);

  return (
    <Card
      key={index}
      onPress={() => {
        handlePress('onPress', {
          record: item,
          indexRecord: index,
        });
      }}
      style={cardWrapperStyle}
    >
      {isLoading ? null : Platform.OS === 'ios' || Platform.OS === 'web' ? (
        <ContentLoader
          speed={3}
          backgroundColor="#f3f3f3"
          foregroundColor="#ecebeb"
          height={115}
          style={{
            borderRadius:
              +attrs.attributes?.cardCover?.borderRadius ||
              finalStyle?.borderRadius ||
              0,
          }}
        >
          <Rect
            x="0"
            y="0"
            rx="0"
            ry="0"
            width={cardItemOffset - +itemMargin * 2}
            height={115}
          />
        </ContentLoader>
      ) : null}
      <Card.Cover
        source={{
          uri: cardCoverImage,
        }}
        style={cardCoverStyle}
        onLoad={() => {
          setIsLoading(true);
        }}
      />
      <Card.Content style={cardContentStyle}>
        {title.enabled && (
          <Title
            style={titleTextStyle}
            numberOfLines={title && title.titleLineNum > 1 ? 500 : 1}
          >
            {get(bindingValue, 'title.text')}
          </Title>
        )}
        {subTitle?.enabled && (
          <Subheading
            style={subTitleTextStyle}
            numberOfLines={subTitle && subTitle.subtitleLineNum > 1 ? 500 : 1}
          >
            {get(bindingValue, 'subTitle.text')}
          </Subheading>
        )}
        {subTitle2?.enabled && (
          <Paragraph
            style={subTitle2TextStyle}
            numberOfLines={
              subTitle2 && subTitle2.subtitle2LineNum > 1 ? 500 : 1
            }
          >
            {get(bindingValue, 'subTitle2.text')}
          </Paragraph>
        )}
      </Card.Content>
      {(button1?.enabled || icon1?.enabled || icon2?.enabled) && (
        <Card.Actions style={cardActionStyle}>
          {button1.enabled && (
            <Button
              labelStyle={buttonLabelStyle}
              style={actionsButton1Style}
              mode="contained"
              onPress={() => {
                handlePress('button1', {
                  record: item,
                  indexRecord: index,
                });
              }}
            >
              {get(bindingValue, 'button1.text')}
            </Button>
          )}
          {icon1?.enabled && (
            <IconButton
              icon={icon1.icon || 'camera'}
              color={finalStyle?.iconColor || icon1.color}
              size={20}
              onPress={() => {
                handlePress('icon1', {
                  record: item,
                  indexRecord: index,
                });
              }}
            />
          )}
          {icon2?.enabled && (
            <IconButton
              icon={icon2.icon || 'camera'}
              color={finalStyle?.iconColor || icon2.color}
              size={20}
              onPress={() => {
                handlePress('icon2', {
                  record: item,
                  indexRecord: index,
                });
              }}
            />
          )}
        </Card.Actions>
      )}
    </Card>
  );
};

export default CardItem;
